import { useEffect, useState } from "react";
import type { PersonInputData, IDType } from "../../@lib/types";
import Container from "@mui/material/Container";
import Stack from "@mui/material/Stack";
import Paper from "@mui/material/Paper";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import FormHelperText from "@mui/material/FormHelperText";
import OutlinedInput from "@mui/material/OutlinedInput";
import Button from "@mui/material/Button";
import { FormProvider, Controller } from "react-hook-form";
import CareersForm from "./CareersForm";

import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Attachment } from "../../features";
import {
  ConditionalRenderer,
  CountriesDropdown,
  useLanguageTranslator,
} from "../../components";

export type PersonEditorProps = {
  initialValues?: PersonInputData;
  disabled?: boolean;
  onSave: (data: PersonInputData) => void;
  onCancel: () => void;
  setCareerActionsOld?: any;
};

const yupIdType = yup
  .mixed(
    (input): input is IDType =>
      typeof input === "string" || typeof input === "number"
  )
  .transform((v) => (!v ? undefined : v));

const schema = yup.object({
  firstname: yup.string().required("Required!"),
  lastname: yup.string().required("Required!"),
  title: yup.string().required("Required!"),
  careers: yup
    .array(
      yup.object({
        institutionId: yupIdType.required("Required!"),
        roleId: yupIdType.required("Required!"),
        startDate: yup.string().required("Required!"),
        endDate: yup.string(),
        careerActions: yup
          .array(
            yup.object({
              actionId: yupIdType,
              themeId: yupIdType,
              countryId: yupIdType,
              actionDate: yup.string(),
            })
          )
          .notRequired(),
        careerPositions: yup
          .array(
            yup.object({
              positionId: yupIdType,
              themeId: yupIdType,
              countryId: yupIdType,
              positionDate: yup.string(),
            })
          )
          .notRequired(),
      })
    )
    .required(),
});

const PersonEditor = (props: any) => {
  const [imageAttachmentObject, setImageAttachmentObject] = useState<any>({});

  const { translator } = useLanguageTranslator();

  const {
    initialValues,
    personData,
    onSave,
    onCancel,
    disabled,
    isNotPeopleCreateModified,
    setImageAttachmentDetails,
    imageAttachmentDetails,
  } = props;

  useEffect(() => {
    if (personData?.picture?.originalFilename) {
      setImageAttachmentObject({
        name: personData?.picture?.originalFilename,
      });
    }
  }, [personData]);

  const form = useForm<any>({
    resolver: yupResolver(schema),
    defaultValues: {
      firstname: "",
      lastname: "",
      title: "",
      countryId: "",
      careers: [
        {
          startDate: "",
          endDate: "",
          institutionId: "",
          roleId: "",
        },
      ],
    },
    values: initialValues,
  });

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(onSave)}>
        <Paper
          component={Container}
          sx={{
            py: !isNotPeopleCreateModified ? 8 : 3,
            mb: !isNotPeopleCreateModified ? 0 : 3,
          }}
        >
          <Stack direction={{ md: "row" }} spacing={3}>
            <div style={{ flex: 1 }}>
              <Attachment
                allowedFormats={["svg", "png", "jpg", "gif", "jpeg"]}
                text="SVG, PNG, JPG or GIF (max. 1 MB)"
                setAttachmentDetails={setImageAttachmentDetails}
                attachmentDetails={imageAttachmentDetails}
                variant={{ variant: "outlined", py: 10 }}
                attachmentObject={imageAttachmentObject}
                setAttachmentObject={setImageAttachmentObject}
              />
            </div>
            <Stack spacing={2} flex={1}>
              <Controller
                name="title"
                render={({ field, fieldState: { error } }) => (
                  <FormControl
                    required
                    disabled={disabled}
                    error={Boolean(error)}
                  >
                    <FormLabel>{translator("Title")}</FormLabel>
                    <OutlinedInput
                      {...field}
                      placeholder={`${translator("Enter")} ${translator(
                        "Title"
                      )}`}
                    />
                    <FormHelperText>
                      {error?.message && translator(error.message)}
                    </FormHelperText>
                  </FormControl>
                )}
              />

              <Controller
                name="firstname"
                // rules={{ required: 'Required!' }}
                render={({ field, fieldState: { error } }) => (
                  <FormControl required disabled={disabled} error={!!error}>
                    <FormLabel>{translator("support.First Name")}</FormLabel>
                    <OutlinedInput
                      {...field}
                      placeholder={`${translator("Enter")} ${translator(
                        "support.First Name"
                      )}`}
                    />
                    <FormHelperText>
                      {error?.message && translator(error.message)}
                    </FormHelperText>
                  </FormControl>
                )}
              />

              <Controller
                name="lastname"
                render={({ field, fieldState: { error } }) => (
                  <FormControl
                    required
                    disabled={disabled}
                    error={Boolean(error)}
                  >
                    <FormLabel>{translator("support.Last Name")}</FormLabel>
                    <OutlinedInput
                      {...field}
                      placeholder={`${translator("Enter")} ${translator(
                        "support.Last Name"
                      )}`}
                    />
                    <FormHelperText>
                      {error?.message && translator(error.message)}
                    </FormHelperText>
                  </FormControl>
                )}
              />
              <Controller
                name={`countryId`}
                render={({ field, fieldState: { error } }) => (
                  <FormControl
                    disabled={disabled}
                    error={Boolean(error)}
                    fullWidth
                  >
                    <FormLabel>{translator("Country")}</FormLabel>
                    <CountriesDropdown {...field} />
                    <FormHelperText>
                      {error?.message && translator(error.message)}
                    </FormHelperText>
                  </FormControl>
                )}
              />
            </Stack>
          </Stack>
        </Paper>

        <ConditionalRenderer condition={isNotPeopleCreateModified}>
          <CareersForm name="careers" disabled={disabled} />
        </ConditionalRenderer>

        <Paper component={Container} sx={{ py: 2, mt: 2 }}>
          <Stack direction="row" justifyContent="flex-end" gap={1}>
            <Button
              variant="contained"
              color="inherit"
              sx={{ px: 6 }}
              disabled={disabled}
              onClick={onCancel}
            >
              {translator("Cancel")}
            </Button>
            <Button
              type="submit"
              variant="contained"
              size="large"
              sx={{ px: 6 }}
              disabled={disabled}
            >
              {translator("generic.Save")}
            </Button>
          </Stack>
        </Paper>
      </form>
    </FormProvider>
  );
};

export { PersonEditor };
